import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, NavLink, useNavigate } from "react-router-dom";

const NAV = [
  { label: "About", to: "/about" },
  { label: "Collections", to: "/collections" },
  { label: "Process", to: "/process" },
  { label: "Alterations", to: "/alterations" },
  { label: "Contact", to: "/contact" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, delay: 1.6, ease: [0.22, 1, 0.36, 1] }}
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "backdrop-blur-xl bg-ivory/80 border-b border-line/60" : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-12 flex items-center justify-between h-20 md:h-24">
        <Link to="/" data-testid="logo-button" className="flex items-center gap-3">
          <img src="/nk-logo-dark.png" alt="NK Bridal" className="h-9 md:h-11 w-auto" />
          <span className="hidden sm:block font-serif text-base md:text-lg tracking-[0.18em] leading-tight border-l border-line pl-3">
            BRIDAL<br />AUSTRALIA
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `link-underline text-xs uppercase tracking-[0.25em] font-medium ${isActive ? "text-ink" : "text-ink/60"}`
              }
            >
              {n.label}
            </NavLink>
          ))}
          <button
            onClick={() => navigate("/contact")}
            data-testid="header-book-button"
            className="btn-fill border border-ink px-6 py-3 text-xs uppercase tracking-[0.25em] font-medium hover:text-ivory transition-colors duration-500"
          >
            Book Appointment
          </button>
        </nav>

        <button
          onClick={() => setOpen((o) => !o)}
          data-testid="menu-toggle"
          className="md:hidden flex flex-col gap-[6px] w-8"
          aria-label="Menu"
        >
          <span className={`h-px bg-ink transition-transform duration-300 ${open ? "translate-y-[7px] rotate-45" : ""}`} />
          <span className={`h-px bg-ink transition-opacity duration-300 ${open ? "opacity-0" : ""}`} />
          <span className={`h-px bg-ink transition-transform duration-300 ${open ? "-translate-y-[7px] -rotate-45" : ""}`} />
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.76, 0, 0.24, 1] }}
            className="md:hidden overflow-hidden bg-ivory border-b border-line"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {NAV.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  onClick={() => setOpen(false)}
                  className="font-serif text-3xl text-left"
                  data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                >
                  {n.label}
                </Link>
              ))}
              <button
                onClick={() => { setOpen(false); navigate("/contact"); }}
                className="mt-2 self-start btn-fill border border-ink px-6 py-3 text-xs uppercase tracking-[0.25em] hover:text-ivory transition-colors duration-500"
                data-testid="mobile-book-button"
              >
                Book Appointment
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
